import { useEffect, useMemo, useState } from 'react';
import { fetchCompatiblePrinters } from '@/lib/labelPrint';
import { getBluetoothAvailability } from '@/lib/printing/bluetoothTransport';

async function decoratePrinter(printer) {
  if (printer.connection_type === 'bluetooth') {
    const availability = await getBluetoothAvailability(printer);
    return { ...printer, ...availability };
  }
  return {
    ...printer,
    available: !!printer.online,
    label: printer.online ? 'Online' : 'Offline',
  };
}

export default function useDirectPrinters({ backendUrl, widthMm, heightMm, enabled = true }) {
  const [printers, setPrinters] = useState([]);
  const [selectedId, setSelectedId] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    const refresh = () => setRefreshKey(value => value + 1);
    window.addEventListener('klaro-printer-binding-changed', refresh);
    return () => window.removeEventListener('klaro-printer-binding-changed', refresh);
  }, []);

  useEffect(() => {
    if (!enabled || !widthMm || !heightMm) {
      setPrinters([]); setSelectedId(''); setError('');
      return undefined;
    }
    let alive = true;
    setLoading(true); setError('');
    fetchCompatiblePrinters(backendUrl, widthMm, heightMm)
      .then(items => Promise.all(items.map(decoratePrinter)))
      .then(items => {
        if (!alive) return;
        setPrinters(items);
        setSelectedId(prev => {
          const current = items.find(p => p.printer_id === prev && p.available);
          if (current) return current.printer_id;
          const preferred = items.find(p => p.available && p.is_default) || items.find(p => p.available);
          return preferred?.printer_id || '';
        });
      })
      .catch(e => { if (alive) { setPrinters([]); setSelectedId(''); setError(e.message); } })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [backendUrl, widthMm, heightMm, enabled, refreshKey]);

  const selected = useMemo(() => printers.find(p => p.printer_id === selectedId) || null, [printers, selectedId]);
  const available = printers.filter(p => p.available);
  // `online` remains as a compatibility alias for older callers during the
  // transition; operational UI should use `available`.
  return { printers, available, online: available, selectedId, setSelectedId, selected, loading, error };
}
